// components/ClanCrewQuestionCampaignAnalytics.jsx
// Sprint: View sentiment question campaign analytics and responses
import React, { useState, useMemo } from 'react';
import { Modal, Table, Tag, DatePicker, Empty, Input, Select } from 'antd';
import { BarChart2, Users, CheckCircle, Clock, XCircle, Eye } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Get_User_Persona_Campaigns, Get_User_Persona_Campaign_Details, Get_User_Responses_For_Question } from '../../services/userAPI';
import moment from 'moment';
import './clanCrewQuestionCampaignAnalytics.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

// ============================================================================

const ClanCrewQuestionCampaignAnalytics = ({
    visible,
    onCancel,
}) => {
    const location = useLocation();
    const roleId = location.state?.roleId;
    const accountId = location.state?.accountId;

    const [dateRange, setDateRange] = useState(null);
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [viewingResponses, setViewingResponses] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    const [responseSearchText, setResponseSearchText] = useState('');
    const [responseStatusFilter, setResponseStatusFilter] = useState('');

    // Pagination state for campaigns
    const [campaignPage, setCampaignPage] = useState(1);
    const [campaignPageSize] = useState(10);

    // Pagination state for responses
    const [responsePage, setResponsePage] = useState(1);
    const [responsePageSize] = useState(10);

    // Fetch campaign data from API with pagination
    const { data: campaignData, loading: campaignLoading, error: campaignError } = useQuery(
        Get_User_Persona_Campaigns,
        {
            variables: {
                accountId: parseInt(accountId),
                jobroleId: parseInt(roleId),
                pagingCriteria: {
                    limit: campaignPageSize,
                    page: campaignPage,
                    sortByField: "created_at",
                    sortOrder: "desc"
                }
            },
            skip: !accountId || !roleId,
            fetchPolicy: 'network-only'
        }
    );

    // Fetch campaign details when a campaign is selected
    const [fetchCampaignDetails, { data: campaignDetailsData, loading: campaignDetailsLoading }] = useLazyQuery(
        Get_User_Persona_Campaign_Details,
        {
            fetchPolicy: 'network-only'
        }
    );

    // Fetch user responses for a question
    const [fetchUserResponses, { data: userResponsesData, loading: userResponsesLoading }] = useLazyQuery(
        Get_User_Responses_For_Question,
        {
            fetchPolicy: 'no-cache'
        }
    );

    // Transform API data to match component structure
    const campaigns = useMemo(() => {
        if (!campaignData?.getUserPersonaCampaigns?.data) return [];

        return campaignData.getUserPersonaCampaigns.data.map(campaign => ({
            id: campaign.id,
            createdAt: campaign.createdAt,
            createdByName: campaign.createdBy?.name || '',
            cohortNames: campaign.cohort ? [campaign.cohort.cohortName] : [],
            questionCount: campaign.questionsCount || campaign.questions?.length || 0,
            totalTargeted: campaign.targetedUsersCount || 0,
            totalAnswered: campaign.answeredUsersCount || 0,
            totalNoResponse: campaign.noResponceUsersCount || 0,
        }));
    }, [campaignData]);

    // Get pagination meta for campaigns
    const campaignPageMeta = campaignData?.getUserPersonaCampaigns?.pageMeta;

    // Since pagination is server-side, we use campaigns directly
    // Date range filtering would need to be done server-side as well
    const filteredCampaigns = campaigns;

    // Fetch campaign details when selectedCampaignId changes
    React.useEffect(() => {
        if (selectedCampaignId) {
            fetchCampaignDetails({ variables: { campaignId: parseInt(selectedCampaignId) } });
        }
    }, [selectedCampaignId, fetchCampaignDetails]);

    // Campaign-level summary counts (returned at top level of details response)
    const campaignSummary = useMemo(() => {
        const details = campaignDetailsData?.getUserPersonaQuestionsCampignDetails;
        if (!details) return { totalTargeted: 0, totalAnswered: 0, totalNoResponse: 0, questionsSent: 0 };
        return {
            totalTargeted: details.targetedUsersCount || 0,
            totalAnswered: details.answeredUsersCount || 0,
            totalNoResponse: details.noResponceUsersCount || 0,
            questionsSent: details.questionsCount || details.questions?.length || 0,
        };
    }, [campaignDetailsData]);

    // Get analytics for selected campaign from campaign details API
    const analytics = useMemo(() => {
        if (!selectedCampaignId || !campaignDetailsData?.getUserPersonaQuestionsCampignDetails) return [];

        const campaignDetails = campaignDetailsData.getUserPersonaQuestionsCampignDetails;

        return campaignDetails.questions?.map((question, index) => ({
            id: question.questionId,
            question: {
                id: question.questionId,
                questionText: question.question?.question
            },
            totalTargeted: question.targetedUsersCount || 0,
            totalAnswered: question.answeredUsersCount || 0,
            totalNoResponse: question.noResponceUsersCount || 0,
        })) || [];
    }, [selectedCampaignId, campaignDetailsData]);

    // Get pagination meta for responses
    const responsePageMeta = userResponsesData?.getUserResponcesForQuestionInCampaign?.pageMeta;

    // Get responses for selected question from API
    const responses = useMemo(() => {
        const data = userResponsesData?.getUserResponcesForQuestionInCampaign?.data;
        if (!data || data.length === 0) return [];

        return data.map(item => {
            const question = item.question;
            const user = item.user;
            const responsesArr = question?.responses || [];
            const answersArr = question?.answers || [];

            // Find if this specific user has responded - check if any response matches this user's id
            const userResponse = responsesArr.length > 0
                ? responsesArr.find(r => Number(r.userId) === Number(user?.id))
                : null;

            // Find the answer text for the user's response
            const answer = userResponse
                ? answersArr.find(a => Number(a.id) === Number(userResponse.answerId))
                : null;

            return {
                id: item.id,
                oderId: item.id,
                pushedDate: item.pushedDate,
                userName: (user?.name || "") + " " + (user?.surname || ""),
                userId: user?.id,
                designation: user?.designation,
                employeeId: user?.employeeId,
                profilePicture: user?.profilePicture,
                status: userResponse ? 'ANSWERED' : 'NO_RESPONSE',
                answeredAt: userResponse?.createdAt || null,
                answerText: answer?.answerText || null,
            };
        });
    }, [userResponsesData]);

    // Filter responses by search text and status
    const filteredResponses = useMemo(() => {
        let filtered = responses;

        // Filter by search text (userName or answerText)
        if (responseSearchText) {
            filtered = filtered.filter(r =>
                r.userName?.toLowerCase().includes(responseSearchText.toLowerCase()) ||
                r.answerText?.toLowerCase().includes(responseSearchText.toLowerCase())
            );
        }

        // Filter by status
        if (responseStatusFilter) {
            filtered = filtered.filter(r => r.status === responseStatusFilter);
        }

        return filtered;
    }, [responses, responseSearchText, responseStatusFilter]);

    // Handle viewing responses for a question
    const handleViewResponses = (campaignId, questionId) => {
        setSelectedCampaignId(campaignId);
        setSelectedQuestionId(questionId);
        setViewingResponses(true);
        setResponsePage(1); // Reset to first page
        // Fetch user responses from API
        fetchUserResponses({
            variables: {
                campaignId: parseInt(campaignId),
                questionId: parseInt(questionId),
                pagingCriteria: {
                    limit: responsePageSize,
                    page: 1,
                    sortByField: "created_at",
                    sortOrder: "desc"
                }
            }
        });
    };

    // Handle campaign page change
    const handleCampaignPageChange = (page) => {
        setCampaignPage(page);
    };

    // Handle response page change
    const handleResponsePageChange = (page) => {
        setResponsePage(page);
        fetchUserResponses({
            variables: {
                campaignId: parseInt(selectedCampaignId),
                questionId: parseInt(selectedQuestionId),
                pagingCriteria: {
                    limit: responsePageSize,
                    page: page,
                    sortByField: "created_at",
                    sortOrder: "desc"
                }
            }
        });
    };

    // Campaign list columns
    const campaignColumns = [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => moment(date).format('DD MMM YYYY, hh:mm A'),
            width: 165,
        },
        {
            title: 'Sort by',
            dataIndex: 'createdByName',
            key: 'createdByName',
            width: 100,
        },
        {
            title: 'Cohort',
            dataIndex: 'cohortNames',
            key: 'cohortNames',
            width: 190,
            render: (names) => {
                const fullName = names?.filter(Boolean).join(', ') || '-';
                return <span title={fullName} className="question-cohort-name">{fullName}</span>;
            },
        },
        {
            title: 'Questions',
            dataIndex: 'questionCount',
            key: 'questionCount',
            width: 95,
            align: 'center',
            className: 'question-count-column',
            render: (count) => Number(count) || 0,
        },
        {
            title: 'Targeted Users',
            dataIndex: 'totalTargeted',
            key: 'totalTargeted',
            width: 110,
            align: 'center',
            render: (val) => val ?? 0,
        },
        {
            title: 'Answered',
            dataIndex: 'totalAnswered',
            key: 'totalAnswered',
            width: 85,
            align: 'center',
            render: (val) => val ?? 0,
        },
        {
            title: 'No response',
            dataIndex: 'totalNoResponse',
            key: 'totalNoResponse',
            width: 110,
            align: 'center',
            render: (val) => val ?? 0,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 85,
            render: (_, record) => (
                <button
                    onClick={() => setSelectedCampaignId(record.id)}
                    className="text-[#AD0101] flex items-center gap-1"
                >
                    Details
                </button>
            ),
        },
    ];

    // Analytics (per-question) columns
    const analyticsColumns = [
        {
            title: 'Question',
            dataIndex: ['question', 'questionText'],
            key: 'question',
            ellipsis: true,
        },
        {
            title: 'Targeted',
            dataIndex: 'totalTargeted',
            key: 'totalTargeted',
            width: 100,
            align: 'center',
        },
        {
            title: 'Answered',
            dataIndex: 'totalAnswered',
            key: 'totalAnswered',
            width: 100,
            align: 'center',
            render: (val) => <span className="text-green-600 font-medium">{val}</span>,
        },
        {
            title: 'No Response',
            dataIndex: 'totalNoResponse',
            key: 'totalNoResponse',
            width: 120,
            align: 'center',
            render: (val) => <span className="text-yellow-600 font-medium">{val}</span>,
        },
        // {
        //     title: 'Response Rate',
        //     key: 'rate',
        //     width: 120,
        //     render: (_, record) => {
        //         const rate = record.totalTargeted > 0
        //             ? Math.round((record.totalAnswered / record.totalTargeted) * 100)
        //             : 0;
        //         return (
        //             <div className="flex items-center gap-2">
        //                 <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
        //                     <div
        //                         className="h-full bg-green-500 rounded-full"
        //                         style={{ width: `${rate}%` }}
        //                     />
        //                 </div>
        //                 <span className="text-xs">{rate}%</span>
        //             </div>
        //         );
        //     },
        // },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <button
                    onClick={() => handleViewResponses(selectedCampaignId, record.question?.id)}
                    className="text-[#AD0101] flex items-center gap-1"
                >
                    Responses
                </button>
            ),
        },
    ];

    // Responses columns
    const responsesColumns = [
        {
            title: 'User',
            dataIndex: 'userName',
            key: 'userName',
            width: 200,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status) => {
                const statusConfig = {
                    ANSWERED: { color: 'green', icon: <CheckCircle size={14} />, text: 'Answered' },
                    NO_RESPONSE: { color: 'orange', icon: <Clock size={14} />, text: 'No Response' },
                    SKIPPED_DISABLED: { color: 'red', icon: <XCircle size={14} />, text: 'Skipped (Disabled)' },
                    SKIPPED_COOLING: { color: 'purple', icon: <Clock size={14} />, text: 'Skipped (Cooling)' },
                };
                const config = statusConfig[status] || { color: 'default', text: status };
                return (
                    <Tag color={config.color} className="flex items-center gap-1 w-fit">
                        {config.icon} {config.text}
                    </Tag>
                );
            },
        },
        {
            title: 'Answered at',
            dataIndex: 'answeredAt',
            key: 'answeredAt',
            width: 180,
            render: (date) => date ? moment(date).format('DD MMM YYYY, hh:mm A') : '',
        },
        {
            title: 'Answer',
            dataIndex: 'answerText',
            key: 'answerText',
            ellipsis: true,
            render: (text) => text || <span className="text-gray-400"></span>,
        },
    ];

    const resetView = () => {
        setSelectedCampaignId(null);
        setSelectedQuestionId(null);
        setViewingResponses(false);
        setResponsePage(1);
    };

    return (
        <Modal
            open={visible}
            onCancel={() => { onCancel(); resetView(); }}
            footer={null}
            width={1000}
            centered
            className="clan-crew-question-analytics-modal"
            title={!selectedCampaignId ? 'All Questions Analytics' : 'Question Analytics'}
        >
            <div>
                {/* Breadcrumb Navigation */}
                {selectedCampaignId && <div className="mb-4 flex items-center gap-2 text-sm">
                    <button
                        onClick={resetView}
                        className="text-gray-600 hover:text-[#AD0101]"
                    >
                        All Questions Analytics
                    </button>
                    <span className="text-gray-400">/</span>
                    <button
                        onClick={() => setViewingResponses(false)}
                        className={`${!viewingResponses ? 'font-bold text-[#AD0101]' : 'text-gray-600'}`}
                    >
                        Question Details
                    </button>
                    {viewingResponses && (
                        <>
                            <span className="text-gray-400">/</span>
                            <span className="font-bold text-[#AD0101]">User Responses</span>
                        </>
                    )}
                </div>}

                {/* Campaign List View */}
                {!selectedCampaignId && (
                    <div>
                        {/* Filters */}
                        {/* <div className="mb-4 flex gap-4">
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                placeholder={['From Date', 'To Date']}
                            />
                        </div> */}

                        {/* Campaigns Table */}
                        {campaignLoading ? (
                            <div className="text-center py-8">Loading questions...</div>
                        ) : campaignError ? (
                            <div className="text-center py-12 text-red-600">
                                Error loading questions: {campaignError.message}
                            </div>
                        ) : filteredCampaigns.length === 0 ? (
                            <Empty description="No questions found" />
                        ) : (
                            <Table
                                className="question-campaign-table"
                                dataSource={filteredCampaigns}
                                columns={campaignColumns}
                                rowKey="id"
                                pagination={{
                                    current: campaignPage,
                                    pageSize: campaignPageSize,
                                    total: campaignPageMeta?.totalCount || 0,
                                    onChange: handleCampaignPageChange,
                                    showSizeChanger: false,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                                }}
                                size="small"
                            />
                        )}
                    </div>
                )}

                {/* Campaign Analytics View */}
                {selectedCampaignId && !viewingResponses && (
                    <div>
                        {campaignDetailsLoading ? (
                            <div className="text-center py-8">Loading campaign details...</div>
                        ) : (
                            <>
                                {/* Summary Cards */}
                                <div className="grid grid-cols-4 gap-4 mb-6">
                                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-[#AD0101]">
                                            {analytics.reduce((sum, a) => sum + (a.totalTargeted || 0), 0)}
                                        </div>
                                        <div className="text-sm text-[#AD0101]">Total Targeted</div>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {analytics.reduce((sum, a) => sum + (a.totalAnswered || 0), 0)}
                                        </div>
                                        <div className="text-sm text-green-600">Total Answered</div>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-yellow-600">
                                            {analytics.reduce((sum, a) => sum + (a.totalNoResponse || 0), 0)}
                                        </div>
                                        <div className="text-sm text-yellow-600">No Response</div>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-purple-600">
                                            {analytics.length}
                                        </div>
                                        <div className="text-sm text-purple-600">Questions Sent</div>
                                    </div>
                                </div>

                                {/* Per-Question Analytics Table */}
                                {analytics.length === 0 ? (
                                    <Empty description="No analytics data available" />
                                ) : (
                                    <Table
                                        dataSource={analytics}
                                        columns={analyticsColumns}
                                        rowKey="id"
                                        pagination={false}
                                        size="small"
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* User Responses View */}
                {viewingResponses && (
                    <div>
                        {userResponsesLoading ? (
                            <div className="text-center py-8">Loading responses...</div>
                        ) : (
                            <>
                                {/* Summary */}
                                <div className="grid grid-cols-3 gap-4 mb-6">
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {responses.filter(r => r.status === 'ANSWERED').length}
                                        </div>
                                        <div className="text-sm text-green-600">Answered</div>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-yellow-600">
                                            {responses.filter(r => r.status === 'NO_RESPONSE').length}
                                        </div>
                                        <div className="text-sm text-yellow-600">No Response</div>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-red-600">
                                            {responses.filter(r => r.status?.startsWith('SKIPPED')).length}
                                        </div>
                                        <div className="text-sm text-red-600">Skipped</div>
                                    </div>
                                </div>

                                {/* Filters */}
                                <div className="mb-4 flex gap-3">
                                    <Input
                                        placeholder="Search by user name..."
                                        value={responseSearchText}
                                        onChange={e => setResponseSearchText(e.target.value)}
                                        allowClear
                                        className="flex-1"
                                        style={{ maxWidth: '400px' }}
                                    />
                                    <Select
                                        placeholder="Filter by status"
                                        value={responseStatusFilter || undefined}
                                        onChange={v => setResponseStatusFilter(v || '')}
                                        allowClear
                                        style={{ width: '200px' }}
                                    >
                                        <Option value="ANSWERED">Answered</Option>
                                        <Option value="NO_RESPONSE">No Response</Option>
                                        <Option value="SKIPPED_DISABLED">Skipped (Disabled)</Option>
                                        <Option value="SKIPPED_COOLING">Skipped (Cooling)</Option>
                                    </Select>
                                    {responses.length > 0 && responses[0]?.pushedDate && (
                                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                                            <span className="font-medium">Pushed On:</span>
                                            <span>{moment(responses[0].pushedDate).format('DD MMM YYYY')}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Responses Table */}
                                {responses.length === 0 ? (
                                    <Empty description="No responses data available" />
                                ) : filteredResponses.length === 0 ? (
                                    <Empty description="No responses match the selected filters" />
                                ) : (
                                    <Table
                                        dataSource={filteredResponses}
                                        columns={responsesColumns}
                                        rowKey="id"
                                        pagination={{
                                            current: responsePage,
                                            pageSize: responsePageSize,
                                            total: responsePageMeta?.totalCount || 0,
                                            onChange: handleResponsePageChange,
                                            showSizeChanger: false,
                                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                                        }}
                                        size="small"
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    )
};

export default ClanCrewQuestionCampaignAnalytics;
